<template>
  <div
    class="p-3 text-body-1 text-truncate cursor-pointer align-self-center"
    @click="redirectToPatientDetails"
  >
    <p class="text-primary font-semibold text-wrap">{{ getFormattedPatientName(caze.patient) }}</p>
    <div v-if="isSidebarVisible" @click.stop>
      <ActionIcons
        :config="actionIconsConfig"
        @create-consultation-click="onCreateConsultation"
        @emergency-click="emit('emergency-click', caze.id)"
        @edit-patient-click="onEditPatient"
        @close-case-click="onCloseCase"
      />
    </div>
  </div>

  <CloseCaseModal
    :show="showCloseModal"
    @update:show="showCloseModal = $event"
    @confirm="handleCloseCaseConfirm"
  />

  <VTooltip
    :text="INV_BP.hasMeasurement ? INV_BP.displayDate : NON_INV_BP.displayDate"
    :location="'bottom center'"
    :open-delay="500"
    :offset="-8"
  >
    <template #activator="{ props }">
      <SystolicPressureDisplay
        v-bind="props"
        :systolic-pressure="INV_BP.hasMeasurement ? INV_BP : NON_INV_BP"
        :is-invasive="INV_BP.hasMeasurement"
      />
    </template>
  </VTooltip>

  <VTooltip
    :text="HEART_RATE.displayDate"
    :location="'bottom center'"
    :open-delay="500"
    :offset="-8"
  >
    <template #activator="{ props }">
      <div v-bind="props" class="justify-self-start align-self-center p-3 text-body-1 w-100">
        {{ formatValue(HEART_RATE.displayValue, NumberFormat.INTEGER) }}
      </div>
    </template>
  </VTooltip>

  <VTooltip :text="SPO2.displayDate" :location="'bottom center'" :open-delay="500" :offset="-8">
    <template #activator="{ props }">
      <div v-bind="props" class="justify-self-start align-self-center p-3 text-body-1 w-100">
        {{ formatValue(SPO2.displayValue, NumberFormat.INTEGER) }}
      </div>
    </template>
  </VTooltip>

  <VTooltip :text="RR.displayDate" :location="'bottom center'" :open-delay="500" :offset="-8">
    <template #activator="{ props }">
      <div v-bind="props" class="justify-self-start align-self-center p-3 text-body-1 w-100">
        {{ formatValue(RR.displayValue, NumberFormat.INTEGER) }}
      </div>
    </template>
  </VTooltip>

  <VTooltip :text="TEMP.displayDate" :location="'bottom center'" :open-delay="500" :offset="-8">
    <template #activator="{ props }">
      <div v-bind="props" class="justify-self-start align-self-center p-3 text-body-1 w-100">
        {{ formatValue(TEMP.displayValue, NumberFormat.DECIMAL) }}
      </div>
    </template>
  </VTooltip>

  <VTooltip
    v-if="isEdsEnabled"
    :text="edsTooltipText"
    location="bottom center"
    :open-delay="500"
    :offset="-8"
    :disabled="!hasAcceptedEdsLimitation"
  >
    <template #activator="{ props }">
      <div
        v-bind="props"
        data-testid="eds-column"
        class="justify-self-start align-self-center p-3 text-body-1 w-100"
      >
        <button
          data-testid="open-eds-sidebar-btn"
          v-if="!hasAcceptedEdsLimitation"
          class="mx-auto"
          @click="emit('open-eds-sidebar')"
        >
          <VIcon
            class="material-icons-outlined"
            icon="visibility_off"
            size="18px"
            color="fg-disabled"
          />
        </button>
        <template v-else>
          {{ formatValue(edsScore?.scoreValue, NumberFormat.INTEGER) }}
        </template>
      </div>
    </template>
  </VTooltip>
  <div class="flex align-center flex-wrap gap-1 p-3 xl:min-w-0">
    <p v-if="!organSupportList.length">
      {{ '–' }}
    </p>

    <OrganSupportChip
      v-else
      v-for="organSupport in organSupportList"
      :key="organSupport"
      :text="organSupport"
    />
  </div>

  <div v-if="!isSidebarVisible" class="flex items-center justify-start gap-3 ml-[15px]">
    <ActionIcons
      :config="actionIconsConfig"
      @create-consultation-click="onCreateConsultation"
      @emergency-click="emit('emergency-click', caze.id)"
      @edit-patient-click="onEditPatient"
      @close-case-click="onCloseCase"
    />
  </div>

  <DividerLine class="col-span-full" />
</template>

<script setup lang="ts">
import { computed, ref, toRef } from 'vue'
import type {
  CaseDTO,
  ClinicalScoreResultDto,
  Discharge,
  OrganSupportDTO,
  VitalSignItemDTO
} from '@/generated'
import { NoticeType } from '@/stores/models/Notice'
import { useNoticeStore } from '@/stores/notice'
import { useUserStore } from '@/stores/user'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { routeNames } from '@/router/routeNames'
import DividerLine from '@/atoms/DividerLine.vue'
import { usePatientVitals } from '../../composables/usePatientVitals'
import SystolicPressureDisplay from '../atoms/SystolicPressureDisplay.vue'
import { useI18n } from 'vue-i18n'
import { useFormatValue } from '@/composables/useFormatValue'
import { NumberFormat } from '@/i18n/i18n'
import useFormattedPatientDetails from '@/composables/useFormattedPatientDetails'
import CloseCaseModal from '@/components/patient/components/molecules/CloseCaseModal.vue'
import OrganSupportChip from '@/components/patient/components/molecules/OrganSupportChip.vue'
import { parseOrganSupportDTO } from '@/util/parseOrganSupportDTO'
import { useEdsPlugin } from '@/composables/useEdsPlugin'
import ActionIcons from '../atoms/ActionIcons.vue'
import { usePluginsStore } from '@/stores/plugins'

interface Emits {
  (e: 'edit-patient', caseId: string): void
  (e: 'close-case', caseId: string, discharge: Discharge): void
  (e: 'emergency-click', caseId: string): void
  (e: 'open-eds-sidebar'): void
}

const props = defineProps<{
  caze: CaseDTO
  vitalSigns: VitalSignItemDTO[]
  organSupport: OrganSupportDTO
  isSidebarVisible: boolean
  edsScore?: ClinicalScoreResultDto
}>()

const emit = defineEmits<Emits>()
const { t } = useI18n()
const { formatValue } = useFormatValue()
const { getFormattedPatientName } = useFormattedPatientDetails()

const userStore = useUserStore()
const { hasHospitalPhysicianRole, canCreateConsultations } = storeToRefs(userStore)
const { isConsultationDisabledForPatient } = userStore
const noticeStore = useNoticeStore()
const { isConnectEnabled, isEdsEnabled } = storeToRefs(usePluginsStore())
const router = useRouter()
const { hasAcceptedEdsLimitation, getEdsScoreTooltipText } = useEdsPlugin()

const edsTooltipText = computed(() => getEdsScoreTooltipText(props.edsScore))

const showCloseModal = ref(false)

const isConsultationDisabled = computed(() => {
  return isConsultationDisabledForPatient(props.caze.hospital?.id)
})

const organSupportList = computed<string[]>(() => {
  return parseOrganSupportDTO(props.organSupport, t)
})

const actionIconsConfig = computed(() => ({
  canCreateConsultations: canCreateConsultations.value && isConnectEnabled.value,
  isConsultationDisabled: isConsultationDisabled.value,
  isEmergencyDisabled: props.caze.emergencyRoutingUnavailable,
  shouldAllowPatientActions:
    hasHospitalPhysicianRole.value &&
    canCreateConsultations.value &&
    isManuallyCreatedPatient.value,
  isManuallyCreatedPatient: isManuallyCreatedPatient.value
}))

const reactiveVitalSigns = toRef(props.vitalSigns)
const { INV_BP, NON_INV_BP, HEART_RATE, SPO2, RR, TEMP } = usePatientVitals(reactiveVitalSigns)

async function onCreateConsultation() {
  try {
    await router.push({
      name: routeNames.NEW_CONSULTATION,
      query: { caseId: props.caze.id }
    })
  } catch (error) {
    console.error((error as Error).message)
    noticeStore.add({
      type: NoticeType.ERROR,
      message: 'Failed to navigate to consultation request'
    })
  }
}

const isManuallyCreatedPatient = computed(() => {
  return props.caze.createdViaApi === true
})

function onEditPatient() {
  emit('edit-patient', props.caze.id)
}

function onCloseCase() {
  showCloseModal.value = true
}

async function handleCloseCaseConfirm(reason: Discharge) {
  emit('close-case', props.caze.id, reason)
}

async function redirectToPatientDetails() {
  await router.push({
    name: routeNames.PATIENT_DETAILS,
    params: { caseId: props.caze.id }
  })
}
</script>

<style lang="scss" scoped>
.icon-tooltip > ::v-deep(.v-overlay__content) {
  background: rgb(31 41 55) !important;
}

.v-tooltip > ::v-deep(.v-overlay__content) {
  background: black;
}
</style>
